/**
 * Clause library — the canonical list of every checklist clause across PV
 * and Battery sections, plus the canned non-compliance recommendation
 * wording per clause.
 *
 * Sources:
 *   - Battery Section 5 (DC Coupled): parsed from
 *     `Recomendation - BS Battery Section 5 - Dc coupled.docx` (April 2026,
 *     SATCHELL, Cohen). 117 clauses across 3 groups (Documentation,
 *     SB & Labelling, Pre-assembled BS).
 *   - Battery Section 4 (AC Coupled / integrated BESS): parsed from
 *     `Recomendation - BESS Battery Section 4 - Ac coupled.docx` (July 2026).
 *   - CER PV: Dan's `Recommendation - Pv only 2026 v2.docx` (September 2026).
 *
 * This file is the SINGLE SOURCE OF TRUTH the PWA caches offline (via
 * `/api/field/clause-library`), the backend uses for `FieldReport`
 * validation, and any future analytics consumes for normalisation.
 *
 * Don't hand-edit the JSON data. Regenerate PV via
 * `scripts/import-pvd-recommendations.py`; the source Word file is retained
 * in `docs/reference/pvd/`. Regenerate Battery Section 4/5 via
 * `scripts/import-battery-recommendations.py`; Word sources live in
 * `docs/reference/battery/`.
 */
import { z } from 'zod';
import { ChecklistSectionSchema } from './field.js';
import section5Data from './clause-library-data.json' with { type: 'json' };
import section4Data from './clause-library-section4-data.json' with { type: 'json' };
import pvData from './clause-library-pv-data.json' with { type: 'json' };
/**
 * One entry in the clause library. The clauseId is unique within a section
 * but not necessarily across sections (PV and Battery may both have, say,
 * "SIGN5" — disambiguate with the section field).
 */
export const ClauseLibraryEntrySchema = z.object({
    section: ChecklistSectionSchema,
    /**
     * Stable id for cross-referencing. For Battery sections this is the
     * concatenation "<group> <itemNumber>" exactly as the recommendations
     * doc and the printed checklist render it — e.g. "Pre-assembled BS 78",
     * "SB & Labelling 13", "Documentation 1". For CER PV this matches the
     * keys in ChecklistClauseMap — e.g. "SIGN5.1", "WIRING15.1".
     */
    clauseId: z.string().min(1),
    /** Visual grouping in the inspector UI. */
    group: z.string().min(1),
    /** Numeric position within the group. String because PV uses "5.1.1". */
    itemNumber: z.string().min(1),
    /** AS/NZS reference, exactly as it appears on the printed checklist. */
    standardRef: z.string().min(1),
    /** The Compliant question, e.g. "Has the homeowner been provided…?". */
    question: z.string().min(1),
    /** True when the portal clause exists but UAT has not confirmed the checklist question yet. */
    questionNeedsReview: z.boolean().optional(),
    /**
     * Optional condensed version of the question. When set, the inspector PWA
     * shows this at default font; the full question lives behind the `?` toggle.
     * When absent, `deriveShortQuestion()` produces an automatic shortened
     * version by stripping inline standard references and capping length.
     *
     * Authoring guidance: keep under 70 characters, drop the AS/NZS clause
     * references entirely (they're shown in the meta row), strip preambles
     * like "in accordance with…". For an inspector who already knows the
     * standard, the short question only has to remind them WHICH check this
     * is — they don't need the legal recitation.
     */
    shortQuestion: z.string().optional(),
    /**
     * Pre-canned recommendation wordings for non-compliances. The inspector
     * sees these as suggestions; they can pick one, edit it, or write their
     * own. Order matters — the most-common wording should come first.
     * Empty array means no canned wording is available.
     */
    cannedRecommendations: z.array(z.string()),
});
/**
 * Produce a short version of a clause's question for compact display.
 * Prefers the authored `shortQuestion` when present; otherwise applies a
 * conservative simplification to the full question.
 *
 * Heuristic:
 *  - Strip parenthetical standard references like "(refer AS/NZS 3000:2018 Cl H4)"
 *  - Strip inline "in accordance with AS/NZS …" / "as required by AS/NZS …"
 *  - Normalise whitespace
 *  - Cap at ~75 chars at a word boundary, append "…"
 *
 * Pure function; safe to call in render.
 */
export function deriveShortQuestion(entry) {
    if (entry.shortQuestion && entry.shortQuestion.trim().length > 0) {
        return entry.shortQuestion;
    }
    let s = entry.question;
    // Strip parenthetical standard references first — they often nest
    // dotted clause numbers that the next regex would scoop up incorrectly.
    s = s.replace(/\s*\((?:refer\s+)?AS\/NZS[^)]*\)\s*/gi, ' ');
    // Strip inline "in accordance with AS/NZS …" / "as required by AS/NZS …"
    // The standard reference can include version numbers (e.g. "5000.2"),
    // optional ":YYYY", optional "Clause N.M.K", and trailing voltage / size
    // qualifiers. Match greedily up to the first sentence-ending punctuation.
    s = s.replace(/\s+(?:in accordance with|as required by)\s+AS\/NZS\s+[A-Za-z0-9.:]+(?:\s+(?:Clause\s+)?[A-Za-z0-9.()/\s-]*)*?(?=[,;?]|\s+(?:and|or)\b|$)/gi, '');
    // Normalise whitespace
    s = s.replace(/\s+/g, ' ').trim();
    // Cap at 75 chars at a word boundary
    const MAX = 75;
    if (s.length > MAX) {
        const lastSpace = s.lastIndexOf(' ', MAX);
        const cut = lastSpace > MAX - 20 ? lastSpace : MAX;
        s = `${s.slice(0, cut).trimEnd()}…`;
    }
    return s;
}
const section5Raw = section5Data;
const section4Raw = section4Data;
const pvRaw = pvData;
/* ────────────────────────────────────────────────────────────────────────────
 * Battery Section 5 — DC Coupled
 * ──────────────────────────────────────────────────────────────────────────── */
const batterySection5 = section5Raw.map((c) => ({
    section: 'battery-section-5-dc',
    clauseId: c.clauseId,
    group: c.group,
    itemNumber: c.itemNumber,
    standardRef: c.standardRef,
    question: c.question,
    ...(c.questionNeedsReview ? { questionNeedsReview: true } : {}),
    cannedRecommendations: c.recommendations,
}));
/* ────────────────────────────────────────────────────────────────────────────
 * Battery Section 4 — AC Coupled / pre-assembled integrated BESS
 * ──────────────────────────────────────────────────────────────────────────── */
const batterySection4 = section4Raw.map((c) => ({
    section: 'battery-section-4-ac',
    clauseId: c.clauseId,
    group: c.group,
    itemNumber: c.itemNumber,
    standardRef: c.standardRef,
    question: c.question,
    ...(c.questionNeedsReview ? { questionNeedsReview: true } : {}),
    cannedRecommendations: c.recommendations,
}));
/* ────────────────────────────────────────────────────────────────────────────
 * CER PV checklist
 * ──────────────────────────────────────────────────────────────────────────── */
const cerPv = pvRaw.map((c) => ({
    section: 'cer-pv',
    clauseId: c.clauseId,
    group: c.group,
    itemNumber: c.itemNumber,
    standardRef: c.standardRef,
    question: c.question,
    ...(c.questionNeedsReview ? { questionNeedsReview: true } : {}),
    cannedRecommendations: c.recommendations,
}));
/* ────────────────────────────────────────────────────────────────────────────
 * Public surface
 * ──────────────────────────────────────────────────────────────────────────── */
/** Every clause across every section, in insertion order. */
export const ClauseLibrary = [
    ...cerPv,
    ...batterySection4,
    ...batterySection5,
];
/** Build a {section}|{clauseId}-keyed lookup map. Memoised at module init. */
const lookupKey = (section, clauseId) => `${section}|${clauseId.trim()}`;
const lookupMap = new Map();
for (const entry of ClauseLibrary) {
    lookupMap.set(lookupKey(entry.section, entry.clauseId), entry);
}
/**
 * Resolve a (section, clauseId) pair to its library entry. Returns undefined
 * if no match — the PWA's submit-validation surface treats this as a hard
 * error (the inspector shouldn't be able to respond to a clause that doesn't
 * exist in the library).
 */
export function findClause(section, clauseId) {
    return lookupMap.get(lookupKey(section, clauseId));
}
/** All clauses in a given section, in insertion order. */
export function clausesInSection(section) {
    return ClauseLibrary.filter((c) => c.section === section);
}
/**
 * All clauses in a given section, grouped by their visual group. The UI uses
 * this to render the section as a list of collapsible group headers.
 */
export function clausesInSectionByGroup(section) {
    // Dan's PV sequence deliberately interleaves categories. Grouping all
    // GENERAL/DC/etc. entries together would reorder the checklist overview
    // relative to Next/Previous navigation and the source document.
    if (section === 'cer-pv') {
        return new Map([['PV checklist', [...clausesInSection(section)]]]);
    }
    const out = new Map();
    for (const entry of clausesInSection(section)) {
        const list = out.get(entry.group) ?? [];
        list.push(entry);
        out.set(entry.group, list);
    }
    return out;
}
//# sourceMappingURL=clause-library.js.map